import Model, { attr, belongsTo } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import type { ModelRegistry } from 'ember-data/types/registries/model';

export type BookingDotComLinkStatus =
  | 'draft'
  | 'capturing_hotel_id'
  | 'captured_hotel_id'
  | 'awaiting_confirmation'
  | 'connection_confirmed'
  | 'all_rooms_matched'
  | 'room_matched'
  | 'connection_locked'
  | 'room_not_active'
  | 'rate_not_syncable';

export interface IUnmatchedRoom {
  id: string;
  name: string;
}

export interface IEligibleRatePlan {
  id: string;
  name: string;
}

export default class BookingDotComLinkModel extends Model {
  @attr('string') hotelId!: string;
  @attr('string') matchedRoomId!: string;
  @attr('string') matchedRoomName!: string;
  @attr('string') rateId!: string;
  @attr('string') status!: BookingDotComLinkStatus;
  @attr('boolean', { defaultValue: false }) syncLosDiscounts!: boolean;
  @attr('array') unmatchedRooms!: IUnmatchedRoom[];
  @attr('array') eligibleRatePlans!: IEligibleRatePlan[];
  @attr('string') channelConnectionId?: string | null;

  @belongsTo('property', { inverse: 'bookingDotComLink', async: false })
  property!: ModelRegistry['property'];

  @cached
  get isConnected(): boolean {
    return [
      'captured_hotel_id',
      'awaiting_confirmation',
      'connection_confirmed',
      'room_matched',
      'all_rooms_matched',
      'connection_locked',
      'room_not_active',
      'rate_not_syncable',
    ].includes(this.status);
  }

  @cached
  get isDraft(): boolean {
    return this.status === 'draft';
  }

  @cached
  get isCapturing(): boolean {
    return this.status === 'capturing_hotel_id';
  }

  @cached
  get isCaptured(): boolean {
    return this.status === 'captured_hotel_id';
  }

  @cached
  get isAwaitingConfirmation(): boolean {
    return this.status === 'awaiting_confirmation';
  }

  @cached
  get isConnectionConfirmed(): boolean {
    return this.status === 'connection_confirmed';
  }

  @cached
  get isRoomMatched(): boolean {
    return this.status === 'room_matched';
  }

  @cached
  get isAllRoomsMatched(): boolean {
    return this.status === 'all_rooms_matched';
  }

  @cached
  get isConnectionLocked(): boolean {
    return this.status === 'connection_locked';
  }

  @cached
  get isRoomNotActive(): boolean {
    return this.status === 'room_not_active';
  }

  @cached
  get isRateNotSyncable(): boolean {
    return this.status === 'rate_not_syncable';
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    'booking-dot-com-link': BookingDotComLinkModel;
  }
}
